<template>
  <div
    class="table-wrapper tenancy-e-circular-contacts"
    v-if="enableEcircularFeature"
  >
    <div class="table-caption">{{ $t('tenancy.table.eCircularContacts') }}</div>
    <div
      class="link__contact"
      v-if="allowLinkContacts"
    >
      <button
        type="button"
        class="btn link__contact--btn"
        :disabled="!tenancyId"
        @click="linkFunc({isCircular: true})"
      >{{ $t('tenancy.table.linkContact') }}</button>
      <span class="link__contact--plus">+</span>
    </div>
    <table>
      <thead>
        <tr>
          <th
            v-for="(header, index) in itemHeaders"
            :class="header.class"
            :key="index"
          >{{ header.name }}</th>
          <th />
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="(item, index) in eCircularContacts"
          :key="index"
        >
          <td class="select-column">
            <input
              type="radio"
              v-hkldinput
              name="primaryPerson"
              :disabled="!allowLinkContacts"
              :checked="deep(item, 'CompanyContact.cPrimary')"
              @change="event => updatePrimaryPerson(index, !deep(item, 'CompanyContact.cPrimary'))"
            >
          </td>
          <td class="number-column">
            <div v-if="canAccessContact">
              <a
                class="name-hyper-link"
                @click="navToContactDetail(item.id, true)"
              >{{ displayI18nFullName(item.firstName, item.lastName) || item.name }}</a>
              <i
                class="remark-included-flag"
                v-if="isEngineer"
                v-show="!!item.remarks"
              />
            </div>
            <div v-else>
              {{ displayI18nFullName(item.firstName, item.lastName) || item.name }}
            </div>
          </td>
          <td class="number-column">{{ item.phone || '--' }}</td>
          <td class="select-ecircular-emails">
            <HKLMultiSelect
              v-if="allowLinkContacts"
              class="multi-value"
              :class="deep(item, 'CompanyContact.cEmails').length === 0 ? 'error-highlight-floor' : ''"
              :value="deep(item, 'CompanyContact.cEmails')"
              :options="emailHklOptions(item)"
              placeholder=""
              @change="val => updatePathField(index, 'cEmails', val)"
            />
            <span v-else>{{ deep(item, 'CompanyContact.cEmails').join(', ') || '--' }}</span>
          </td>
          <td class="select-ecircular-floors">
            <HKLMultiSelect
              class="multi-value"
              :class="isFloorEmptyExisting && deep(item, 'CompanyContact.cFloor').length === 0 ? 'error-highlight-floor' : ''"
              :disabled="!allowLinkContacts"
              :value="deep(item, 'CompanyContact.cFloor')"
              :options="floorHklOptions"
              placeholder=""
              @change="val => updatePathField(index, 'cFloor', val)"
            />
          </td>
          <td class="icon-remove">
            <i
              class="fa fa-times-thin"
              v-if="allowLinkContacts"
              @click="removeConfirm(index)"
            />
          </td>
        </tr>
      </tbody>
    </table>
    <Modal
      ref="removePrimaryContactModal"
      :modalId="modalId"
    >
      <div
        slot="content"
        class="popup-hint-label"
      >
        <span>{{ $t('deleteConfirmMsg') }}</span>
      </div>
      <div slot="btns">
        <button
          class="btn btn--darkblue"
          @click="closeRemovePrimaryModal()"
        >{{ $t('action.cancel') }}</button>
        <button
          class="btn btn--orange"
          @click="removeContact(deletingPrimaryContactIndex)"
        >{{ $t('action.yes') }}</button>
      </div>
    </Modal>
  </div>
</template>

<script>
import lodash from 'lodash'
import { mapGetters, mapMutations } from 'vuex'

import HKLMultiSelect from '@/controls/Select/HKLMultiSelect.vue'
import Modal from '@/controls/Modal'

import mutationTypes from '@/store/mutation-types'
import notification from '@/utils/notification'
import { getECircularEmailSettings } from '@/api/settings/request'
import { utils as formUtils } from '@/plugin/InputChange'

import navToContactDetailMixin from './navToContactDetailMixin'
import formFullnameMixin from '@/pages/formFullnameMixin'

export default {
  name: 'ECircularContact',
  components: {
    HKLMultiSelect,
    Modal,
  },
  props: {
    tenancyId: [String, Number],
    companyId: [String, Number],
    allowLinkContacts: Boolean,
    linkFunc: Function,
  },
  mixins: [navToContactDetailMixin, formFullnameMixin],
  data () {
    const modalId = `primary-contact-${new Date().getTime()}-${Math.floor(Math.random() * 1000)}`
    return {
      deep,
      modalId,
      eCircularEmailsLimit: 7,
      positionOptions: [],
      isFloorEmptyExisting: false,
      isEmailsEmpty: false,
      deletingPrimaryContactIndex: -1,
    }
  },
  methods: {
    ...mapMutations('ccd', {
      updateTenancyContactPathField: mutationTypes.TENANCY_CONTACT_UPDATE_PATH_FIELD,
    }),
    updatePathField (index, path, val) {
      this.updateTenancyContactPathField({
        index: index,
        path: [path],
        newValue: val,
      })
      formUtils.setDirty()
    },
    closeRemovePrimaryModal () {
      this.deletingPrimaryContactIndex = -1
      this.$refs.removePrimaryContactModal.close()
    },
    openRemovePrimaryModal (index) {
      this.deletingPrimaryContactIndex = index
      this.$refs.removePrimaryContactModal.open()
    },
    removeConfirm (index) {
      this.openRemovePrimaryModal(index)
    },

    removeContact (index) {
      if (deep(this.eCircularContacts[index], 'CompanyContact.cPrimary') && Object.keys(this.eCircularContacts).length > 1) {
        if (index === Object.keys(this.eCircularContacts).length - 1) {
          this.updatePrimaryPerson(0, true)
        } else {
          this.updatePrimaryPerson(Number(index) + 1, true)
        }
      }
      this.updatePathField(index, 'isCircular', false)
      this.updatePathField(index, 'cEmails', [])
      this.closeRemovePrimaryModal()
    },
    updatePrimaryPerson (indexToUpdate, value) {
      Object.keys(this.eCircularContacts).forEach(index => {
        this.updatePathField(index, 'cPrimary', false)
      })
      this.updatePathField(indexToUpdate, 'cPrimary', value)
    },
    emailHklOptions (item) {
      return (item.allEmails || []).map((email) => ({ name: email, value: email }))
    },
    isMandatoryEmpty (attr) {
      return Object.keys(this.eCircularContacts).some(index => deep(this.eCircularContacts[index], ['CompanyContact', attr]).length === 0)
    },
    isEmailCountValid () {
      return this.eCircularEmailsCount <= this.eCircularEmailsLimit
    },
    isInvalidFloorEmails () {
      this.isFloorEmptyExisting = this.isMandatoryEmpty('cFloor')
      this.isEmailsEmpty = this.isMandatoryEmpty('cEmails')
      if (!this.isEmailCountValid()) {
        notification.fireNotification(false, this.$t('tenancy.ecircular.exceedsLimit', [`${this.eCircularEmailsLimit}`]))
        return true
      }
      return this.isFloorEmptyExisting || this.isEmailsEmpty
    },
    removeInvalidFloors () {
      Object.keys(this.eCircularContacts).forEach((index) => {
        const list = lodash.cloneDeep(deep(this.currentTenancy, ['tenancyContacts', index, 'CompanyContact', 'cFloor'])) || []
        const valid = list.filter((floor) => this.floorOptions.indexOf(floor) > -1)
        if (valid.length !== list.length) {
          this.updatePathField(index, 'cFloor', valid)
        }
      })
    },
  },
  async mounted () {
    const limit = await getECircularEmailSettings()
    this.eCircularEmailsLimit = limit || this.eCircularEmailsLimit
  },
  computed: {
    ...mapGetters('ccd', ['currentTenancy']),
    ...mapGetters('user', ['isTenant', 'isEngineer']),
    ...mapGetters('regions', ['enableEcircularFeature']),
    itemHeaders () {
      return [{
        name: this.$t('tenancy.table.primary'),
      }, {
        name: this.$t('tenancy.table.name'),
      }, {
        name: this.$t('tenancy.table.priPhone'),
      }, {
        name: this.$t('tenancy.table.ecEmail'),
      }, {
        name: this.$t('tenancy.table.floor'),
      }]
    },
    eCircularContacts () {
      if (!(this.currentTenancy && this.currentTenancy.tenancyContacts)) return {}

      return this.currentTenancy.tenancyContacts.reduce((partial, item, index) => {
        if (item.CompanyContact.isCircular) {
          partial[index] = item
        }
        return partial
      }, {})
    },
    eCircularEmailsCount () {
      return (Object.values(this.eCircularContacts) || []).reduce((count, item) => {
        const result = count + item.CompanyContact.cEmails.length
        return result
      }, 0)
    },
    floorHklOptions () {
      return (this.floorOptions || []).map((floor) => ({ name: floor, value: floor }))
    },
    floorOptions () {
      const premisesDetailList = lodash.get(this.currentTenancy, 'premisesDetailList', [])
      let floors = []
      premisesDetailList.map(item => {
        if (item.floor && floors.indexOf(item.floor) === -1) {
          floors.push(item.floor)
        }
      })
      const buildingFloors = lodash.get(this.currentTenancy, 'building.floor', [])
      floors = lodash.sortBy(floors, floor => {
        if (!floor) {
          return buildingFloors.length + 1
        }
        return buildingFloors.indexOf(floor)
      })
      return floors
    },
  },
  watch: {
    floorOptions () {
      this.removeInvalidFloors()
    },
  },
}
</script>
<style lang="scss" scoped>
@import '~styles/tender/view';
@import '~styles/table';
@import '~styles/multi-value';
@import '~styles/components/tenancy-contacts';

table .number-column {
  width: auto;
  max-width: 200px;
}
.popup-hint-label {
  font-size: 14px;
  font-weight: bold;
  color: #0c3a58;
  margin-bottom: 15px;
}
</style>
<style lang="scss">
.tenancy-e-circular-contacts {
  .error-highlight-floor {
    background-color: #f9ecea;
  }

  .select-ecircular-emails {
    width: 510px;
  }
}

</style>
